/**
 * The view kit (docs/features/views.md): the only elements a view can
 * render. Deskworks draws them with its own components, so every view looks
 * like Deskworks, follows light/dark mode and animates the same way.
 */
export const KIT_COMPONENTS = [
    // layout
    'Stack',
    'Row',
    'Grid',
    'Card',
    'Divider',
    'Split',
    'Scroll',
    // overlays
    'Dialog',
    'Drawer',
    // text
    'Heading',
    'Text',
    'Badge',
    'Markdown',
    'Image',
    'Link',
    'Icon',
    'Callout',
    'Details',
    'Empty',
    'Spinner',
    // numbers
    'Metric',
    'Progress',
    // data
    'BarChart',
    'LineChart',
    'AreaChart',
    'PieChart',
    'Table',
    'List',
    'Board',
    'Timeline',
    // interaction
    'Button',
    'Tabs',
    'Input',
    'Textarea',
    'Select',
    'Checkbox',
    'Switch',
    'Form',
    // free drawing (SVG subset)
    'Svg',
];
/** Lowercase SVG elements allowed inside <Svg>; anything else is dropped by the renderer. */
export const SVG_ELEMENTS = ['g', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'path', 'text', 'tspan'];
/** Attributes allowed on SVG elements (no event handlers except onClick, no href, no style strings). */
export const SVG_ATTRIBUTES = [
    'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'width', 'height', 'd', 'points', 'transform',
    'fill', 'stroke', 'strokeWidth', 'strokeDasharray', 'strokeLinecap', 'opacity', 'fillOpacity', 'textAnchor',
    'fontSize', 'fontWeight', 'dominantBaseline', 'viewBox',
];
/** Colour names a view may use; the renderer maps them to Deskworks's theme (light and dark). */
export const TONES = ['default', 'muted', 'primary', 'success', 'warning', 'danger', 'info', 'accent'];
/** Icons a view may use (<Icon name="check" />, Button icon="send"); the renderer maps them to Deskworks's icon set. */
export const ICONS = [
    'check', 'x', 'plus', 'minus', 'edit', 'trash', 'send', 'search', 'filter', 'refresh', 'download', 'upload', 'link', 'external',
    'eye', 'clock', 'calendar', 'user', 'users', 'mail', 'message', 'bell', 'star', 'heart', 'flag', 'tag', 'file', 'folder',
    'image', 'chart', 'list', 'board', 'settings', 'info', 'warning', 'alert', 'success', 'lock', 'unlock', 'play', 'pause',
    'stop', 'arrow-right', 'arrow-left', 'arrow-up', 'arrow-down', 'chevron-right', 'chevron-down', 'more', 'sparkles', 'rocket',
    'thumbs-up', 'thumbs-down', 'globe', 'euro', 'dollar', 'cart', 'box', 'truck', 'phone', 'home', 'book', 'bot', 'zap',
];
/** Short reference for agents writing views (tool descriptions, prompts). */
export const KIT_REFERENCE = `
A view is a small app: JavaScript or TypeScript with JSX, React-style components and hooks. Deskworks draws it with its
own components (the kit below), so it looks and animates like Deskworks. No fetch, DOM, timers, npm or CSS: everything
comes from the kit. Imports from 'react' or '@deskworks/kit' are allowed and dropped (all names are already there).

  export const queries = { articles: "SELECT id, title, status, created_at FROM articles WHERE created_at > now() - make_interval(days => :days)",
                           article: "SELECT id, title, body FROM articles WHERE id = :id" };
  export const params = { days: 30, id: null };   // defaults; :name in a query is bound safely as a parameter (only declared params)
  export const actions = { … };                   // optional: buttons that do things (see Actions)
  export default function View({ data, meta, params, state, theme, now }) { return <Stack>…</Stack>; }

data.<name>: rows of each query with the params (max 500); meta.<name> = { columns, truncated }.
Components are functions with hooks: useState, useReducer, useMemo, useCallback, useRef, useEffect (all as in React).
  Give list items a key (key={row.id}) so their state stays with them. Props of kit elements are plain values; on* props
  may be functions (they get the clicked row/item/value). Every render and event has 1 s.
Loading more: useQuery(name, params) → { rows, columns, truncated, loading, error, refetch } runs one declared query with
  its own params (e.g. the selected article's body) and loads again when the data changes. setParams({…}) re-runs data.
Work on rows: useWork(table, id) → null or { workItemId, title, status, typeStatus, link, approval: {id, type, description} | null };
  useWork(table, [ids]) → { [id]: … }. status: created, ready, working, waiting, needs_approval, blocked, complete, failed, cancelled.
Other calls: navigate('/w/…') opens a Deskworks page (e.g. work.link); toast('Saved', 'success'|'danger'|'info').

Actions (buttons that do things, for the person who clicks, with their rights; Deskworks asks first where you say so):
  export const actions = {
    publish: { kind: 'update', table: 'articles', label: 'Publish', confirm: 'Publish this article?',
               set: { status: 'published', published_at: '$now', approved_by: '$user' }, when: { status: 'review' } },
    revise:  { kind: 'update', table: 'articles', label: 'Send back', ask: 'What should change?',
               set: { status: 'draft', review_note: '$comment' }, when: { status: 'review' } },
    research:{ kind: 'work',   table: 'articles', label: 'Research again', title: 'Research again: {title}', prompt: 'Research the topic of the linked article again and update its sources.' },
    approve: { kind: 'approve', table: 'articles', label: 'Approve' },
  };
  table: the row's table; the row is named by its primary key (one column).
  kind update: sets the columns in set on the row right away (no agent), e.g. a status button. Values are fixed; '$now',
    '$user', '$userEmail' and '$comment' (the answer to ask) are filled in. when: only while the row still has these values,
    otherwise nothing changes and the person is told (so two people can't publish twice). Use the table's real columns.
  kind work: a new Work Item for the Lead of the view's desk, linked to the row (title may use {column}; prompt = what to
    do; a second click finds the open one). For work that needs an agent: write, research, send, publish to another system.
  kind approve / reject: decides what an agent asked a person to approve (request_approval) on the Work Item linked to the
    row (link_row); approvalType limits it to one type. Only where agents ask for approval; otherwise use update.
  confirm: a yes/no question first; ask: asks for a comment (goes into '$comment', the decision or the Work Item).
  Use: <Button action="approve" row={a.id} /> (label, busy state and result toast come from Deskworks; onClick(result) runs
  after it), <Form action="revise" row={a.id}> with fields, or const approve = useAction('approve'); approve.run(a.id, { comment })
  → { busy, error, result }. Show a button only where it applies (status in the row; useWork(...)?.approval for approve/reject).

Components (props):
  Layout: Stack {gap?, align?} · Row {gap?, align?, justify? start|center|end|between, wrap?} · Grid {columns?=2, gap?}
    Card {title?, subtitle?, tone?} · Divider · Split {side? sm|md|lg|half} (first child = side column, rest = main; stacks on phones)
    Scroll {height?=400} (scrolls inside)
  Overlays: Dialog {open, title?, description?, size? sm|md|lg|xl, onClose} · Drawer {open, title, subtitle?, size?, onClose} (from the right)
  Text: Heading {level?=2} · Text {tone?, size? sm|md|lg, weight? normal|medium|bold} · Badge {tone?} · Markdown {text} (draws ![alt](src) pictures itself)
    Link {to: '/w/…'} or {onClick} · Icon {name, tone?, size? sm|md|lg} · Callout {tone?, title?, icon?} · Details {items [{label, value, format?, tone?}], columns? 1|2}
    Empty {title, hint?, icon?} · Spinner {label?}
  Pictures: Image {src, alt?, caption?, height?, fit? contain|cover}; src = a file of the workspace: its name (cover.png), its
    asset id or address (/files/<id>/…); a path like /api/articles/assets/<slug>/cover.png finds cover.png. Markdown shows
    ![alt](src) the same way, so pass an article's body to Markdown as it is; never turn its pictures into text yourself.
    Other addresses (https://… elsewhere) are never loaded, they show as a placeholder.
  Numbers: Metric {label, value, unit?, delta?, hint?, tone?, format? number|percent} (counts animated) · Progress {value, max?=100, label?, tone?}
  Charts: BarChart {data, x, y, tone?, toneKey?, horizontal?, height?, onClick?(row)} · LineChart / AreaChart {data, x, y (key or keys), height?}
    PieChart {data, label, value, donut?}
  Data: Table {data, columns? [{key, label?, align? left|right, format? number|date|percent}], rowKey?='id', search? (true or placeholder),
      pageSize?, sortable?=true, selected?, onRowClick?(row)}
    List {items [{id, title, subtitle?, badge?, badgeTone?, meta?, icon?, tone?}], selected?, onClick?(item)}
    Board {columns [{key, title, tone?}], items [{id, column, title, subtitle?, tone?}], onClick?(item)} (cards glide between columns)
    Timeline {items [{id, time, title, subtitle?, tone?}]}
  Input: Button {onClick?, action?, row?, input?, variant? primary|outline|ghost|danger|secondary|link, tone?, icon?, size? xs|sm|md|lg, disabled?, loading?, full?}
    Tabs {tabs [{key, label}], value, onChange(key)}
    Input {value?, defaultValue?, name?, label?, hint?, placeholder?, type? text|number|email|search|date|time|url, required?, onChange?(value), onSubmit?(value)}
    Textarea {value?, name?, label?, placeholder?, rows?, required?, onChange?(value)} · Select {value?, options [value | {value, label}], name?, label?, placeholder?, onChange?(value)}
    Checkbox / Switch {checked?, name?, label?, onChange?(bool)}
    Form {onSubmit?(values), action?, row?, submitLabel? (false hides it), onCancel?, cancelLabel?}: fields with a name inside it are collected;
      with action the fields are the input (a field named comment is the comment) and onSubmit gets { values, result }.
    Inputs keep what is typed themselves; set value only to change it from the view (e.g. '' after saving).
  Drawing: Svg {width, height, viewBox?} with g, rect, circle, ellipse, line, polyline, polygon, path, text (fill/stroke may be tones)
Tones: default, muted, primary, success, warning, danger, info, accent (mapped to Deskworks's light/dark theme).
Icons: ${ICONS.join(', ')}.
Helpers: kit.groupBy(rows, key|fn) → [{key, rows, count}], kit.count(rows, key) → [{key, count}], kit.sum, kit.avg, kit.min, kit.max,
  kit.sortBy(rows, key, 'asc'|'desc'), kit.uniq, kit.search(rows, text, keys?), kit.percent(part, total), kit.ageDays(date),
  kit.ageBucket(date) → '<1d'|'1–2d'|'2–7d'|'>7d', kit.day/week/month(date), kit.formatNumber(n), kit.formatDate(d), kit.timeAgo(d).

Example, an approval app (list left, the article right, decide):
  export const queries = { review: "SELECT id, title, created_at FROM articles WHERE status = 'review' ORDER BY created_at",
                           article: "SELECT id, title, body FROM articles WHERE id = :id" };
  export const params = { id: null };
  export const actions = {
    publish: { kind: 'update', table: 'articles', label: 'Publish', confirm: 'Publish this article?',
               set: { status: 'published', published_at: '$now' }, when: { status: 'review' } },
    revise: { kind: 'update', table: 'articles', label: 'Send back', ask: 'What should change?',
              set: { status: 'draft', review_note: '$comment' }, when: { status: 'review' } },
    polish: { kind: 'work', table: 'articles', label: 'Have it polished', title: 'Polish: {title}', prompt: 'Improve the wording of the linked article.' },
  };
  function Article({ id }) {
    const { rows, loading } = useQuery('article', { id });
    const work = useWork('articles', id);
    if (loading) return <Spinner />;
    const a = rows[0];
    if (!a) return <Empty title="Already decided" />;
    return <Card title={a.title}>
      <Markdown text={a.body} />
      <Row>
        <Button action="publish" row={a.id} variant="primary" icon="check" />
        <Button action="revise" row={a.id} icon="arrow-left" />
        {work ? <Badge tone="info">{work.status}</Badge> : <Button action="polish" row={a.id} variant="ghost" icon="sparkles" />}
      </Row>
    </Card>;
  }
  export default function View({ data }) {
    const [id, setId] = useState(null);
    return <Split side="md">
      <List items={data.review.map((r) => ({ id: r.id, title: r.title, meta: kit.timeAgo(r.created_at) }))} selected={id} onClick={(i) => setId(i.id)} />
      {id ? <Article key={id} id={id} /> : <Empty title="Pick an article" icon="file" />}
    </Split>;
  }
`.trim();
//# sourceMappingURL=kit.js.map